<template>
  <div
    class="flex tablet-h:items-center p-2.5 pb-[18px] tablet-h:p-2.5 cursor-pointer tablet-h:hover:bg-[#FAFAFA] rounded-md group border-b border-[#EFEFEF] last:border-none"
    @click="handleClick"
  >
    <div
      class="p-2 min-w-[65px] max-w-[65px] desktop:min-w-[210px] desktop:max-w-[210px] h-[65px] desktop:h-[118px] bg-[#FAFAFA] group-hover:bg-[#F5F5F5] rounded-md flex items-center justify-center mr-4 desktop:mr-5"
    >
      <ThumbnailIcon
        class="w-full h-full"
        :icon-size="iconSize"
        :type="props.file.type"
        :image-url="applyAuthorizationToken(props.file.imageUrl)"
      />
    </div>
    <div class="w-full tablet-h:-mt-0.5">
      <div v-truncate>
        <div
          class="font-secondary text-[16px] desktop:text-[20px] text-gray-800 -mt-1 tablet-h:mt-0 mb-[9px] tablet-h:mb-2.5 line-clamp-4 tablet-h:line-clamp-1 break-all"
          data-truncate-target
        >
          {{ props.file.name }}
        </div>
      </div>
      <div class="flex flex-col tablet-h:flex-row tablet-h:items-center">
        <div class="mb-2.5 desktop:mb-0 tablet-h:w-[200px] mr-3">
          <div
            class="font-primary font-medium text-[10px] desktop:text-[12px] text-gray-800 opacity-60 leading-[18px] mb-0.5 tablet-h:mb-1"
          >
            Owner
          </div>
          <div class="flex items-center leading-[18px]">
            <Avatar
              :size="avatarSize"
              :src="props.file.owner.avatarUrl"
              class="mr-2"
            />
            <div
              v-truncate
              class="grow font-secondary text-[14px] text-gray-800 opacity-60"
            >
              <div data-truncate-target>{{ props.file.owner.name }}</div>
            </div>
          </div>
        </div>
        <div class="mb-[13px] desktop:mb-0 w-[200px]">
          <div
            class="font-primary font-medium text-[10px] desktop:text-[12px] text-gray-800 opacity-60 leading-[18px] mb-0.5 tablet-h:mb-1 flex items-center"
          >
            <Icon :size="12" name="Dacr" class="fill-gray-800 mr-1" />
            Dacr Timestamp
          </div>
          <div class="font-secondary text-[14px] text-gray-800 opacity-60">
            {{ formatTimestamp(props.file.timestamp) }}
          </div>
        </div>
        <div class="mb-2.5 desktop:mb-0 tablet-h:w-[330px] mr-2">
          <div
            class="font-primary font-medium text-[10px] desktop:text-[12px] text-gray-800 opacity-60 leading-[18px] mb-0.5 tablet-h:mb-1"
          >
            Token ID
          </div>
          <div
            class="font-primary text-[15px] text-[#2D2D2D] opacity-60 leading-[18px] break-all"
          >
            {{ truncateMiddle(props.file.id, 30) }}
          </div>
        </div>
        <div>
          <div
            class="font-primary font-medium text-[10px] desktop:text-[12px] text-gray-800 opacity-60 leading-[18px] mb-0.5 tablet-h:mb-1"
          >
            Type
          </div>
          <div class="flex items-center">
            <ThumbnailIcon
              class="w-5 h-5 mr-2"
              :icon-size="20"
              :type="props.file.type"
            />
            <div class="font-secondary text-[14px] text-gray-800 opacity-60">
              {{ formatType(props.file.type) }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <Icon
      :size="26"
      name="ArrowUp"
      class="hidden tablet-h:group-hover:block rotate-90 ml-auto mr-[22px] stroke-gray-800"
    />
  </div>
</template>

<script setup lang="ts">
import type { FileType, SearchFile } from '@/shared/graphql/legacy/generated'
import { ThumbnailIcon, Avatar, Icon } from '@dacr/ui'
import { formatTimestamp } from '@/shared/utils/datetime'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { computed } from 'vue'
import { useDevice } from '@/shared/composables/useDevice'
import { useRouter } from 'vue-router'
import { truncateMiddle } from '@/shared/utils/string'

const props = defineProps<{
  file: SearchFile
}>()

const device = useDevice()
const router = useRouter()

const iconSize = computed(() =>
  device.isMobile.value ||
  device.isTabletVertical.value ||
  device.isTabletHorizontal.value
    ? 55
    : 100
)

const avatarSize = computed(() =>
  device.isTabletHorizontal.value || device.isDesktop.value ? 20 : 18
)

const handleClick = () => {
  router.push(`/file/${props.file.id}`)
}

const formatType = (type: FileType) => {
  return type.charAt(0) + type.substring(1).toLowerCase()
}
</script>
